import { ICON_TOKEN, isIconName, type IconName } from '../../shared/icons.ts';
import { el } from './dom.ts';

/**
 * Icône de l'interface (voir public/assets/icons) : un élément `<i>` dont l'image vient de la feuille
 * générée /build/icons.css. Décorative par défaut ; avec `label`, elle est annoncée comme une image.
 */
export function icon(name: IconName, { label, color, className }: { label?: string; color?: string; className?: string } = {}): HTMLElement {
  const element = el('i', { className: className ? `icon ${className}` : 'icon' });
  element.dataset.icon = name;
  if (label) {
    element.setAttribute('role', 'img');
    element.setAttribute('aria-label', label);
    element.title = label;
  } else element.setAttribute('aria-hidden', 'true');
  if (color) element.style.color = color;
  return element;
}

/** Texte dont les jetons `[[nom]]` deviennent des icônes (les noms inconnus sont retirés). */
export const richText = (text: string): (Node | string)[] => richParts(text).map((part) => (typeof part === 'string' ? part : icon(part.name)));

export function setRichText(element: HTMLElement, text: string): void {
  element.replaceChildren(...richText(text));
}

/** Découpe un texte en unités d'affichage : un caractère, ou une icône entière. */
function textUnits(text: string): unknown[][] {
  return richParts(text).map((part) => (typeof part === 'string' ? [...part] : [part]));
}

export type RichPart = string | { name: IconName };

/** Texte à jetons découpé en morceaux de texte et en icônes (pour les composants Preact, voir ui/components.tsx). */
export function richParts(text: string): RichPart[] {
  const parts: RichPart[] = [];
  let last = 0;
  for (const match of text.matchAll(ICON_TOKEN)) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (isIconName(match[1])) parts.push({ name: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Nombre d'unités d'un texte à jetons (une icône compte pour un caractère), pour l'écrire lettre par lettre. */
export const richLength = (text: string): number => textUnits(text).reduce((total, units) => total + units.length, 0);

/** Les `count` premières unités d'un texte à jetons, prêtes à afficher. */
export function richPrefix(text: string, count: number): (Node | string)[] {
  const nodes: (Node | string)[] = [];
  let left = count;
  for (const part of richParts(text)) {
    if (left <= 0) break;
    if (typeof part === 'string') {
      const chars = [...part].slice(0, left);
      nodes.push(chars.join(''));
      left -= chars.length;
    } else {
      nodes.push(icon(part.name));
      left--;
    }
  }
  return nodes;
}

/** Icône d'un badge d'arène (repli sur l'icône générique si son nom n'existe plus). */
export const badgeIcon = (name: string): HTMLElement => icon(isIconName(name) ? name : 'badge');

/** Contenu d'un bouton ou d'un titre : une icône suivie de son libellé. */
export const withIcon = (name: IconName, text: string): (Node | string)[] => [icon(name), ` ${text}`];

// ---------- Icônes dessinées sur un canevas ----------

interface ManifestEntry {
  url: string;
  colored: boolean;
}

let manifest: Record<string, ManifestEntry> | null = null;
let manifestRequested = false;
const images = new Map<string, HTMLImageElement>();
const tinted = new Map<string, HTMLCanvasElement>();
const listeners = new Set<() => void>();
const notify = () => { for (const listener of listeners) listener(); };

function loadManifest(): void {
  if (manifestRequested) return;
  manifestRequested = true;
  void fetch('/build/icons.json').then((response) => response.json() as Promise<Record<string, ManifestEntry>>).then((data) => {
    manifest = data;
    notify();
  }).catch(() => { manifestRequested = false; });
}

/** `listener` est appelé quand une icône de canevas devient disponible (pour redessiner). */
export function onIconsLoaded(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** L'icône prête à dessiner, teinte de `color` si elle est monochrome (null tant qu'elle charge). */
export function iconImage(name: IconName, color: string, size = 48): CanvasImageSource | null {
  loadManifest();
  const entry = manifest?.[name];
  if (!entry) return null;
  let image = images.get(entry.url);
  if (!image) {
    image = new Image();
    image.addEventListener('load', notify);
    image.src = entry.url;
    images.set(entry.url, image);
  }
  if (!image.complete || !image.naturalWidth) return null;
  if (entry.colored) return image;
  const key = `${name}|${color}|${size}`;
  let canvas = tinted.get(key);
  if (!canvas) {
    canvas = Object.assign(document.createElement('canvas'), { width: size, height: size });
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(image, 0, 0, size, size);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, size, size);
    tinted.set(key, canvas);
  }
  return canvas;
}

/** Dessine l'icône centrée sur (cx, cy) ; renvoie faux si elle n'est pas encore chargée. */
export function drawIcon(ctx: CanvasRenderingContext2D, name: IconName, cx: number, cy: number, size: number, color: string): boolean {
  const image = iconImage(name, color, Math.max(24, Math.ceil(size / 24) * 24));
  if (!image) return false;
  ctx.drawImage(image, Math.round(cx - size / 2), Math.round(cy - size / 2), Math.round(size), Math.round(size));
  return true;
}
